import {
    Box,
    Button,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';

import SandboxedHtmlPreview from '../components/SandboxedHtmlPreview';
import { useDeveloperApi } from './api';
import {
    CodeBlock,
    DetailDrawer,
    DeveloperShell,
    ErrorAlert,
    Field,
    LoadMore,
    Loading,
    mono,
    TableScroll,
    Timestamp,
    ToneChip,
} from './components';
import { OUTBOX_CHANNELS, OUTBOX_OUTCOMES, OUTBOX_REASONS, outboxOutcome, SEND_KINDS } from './labels';
import { DEVELOPER_PATHS } from './routes';
import type { OutboxDestination, OutboxSend, OutboxSendDetail } from './types';
import { useCursorList } from './useCursorList';

const OutcomeChip = ({ outcome }: { outcome: string }) => {
    const label = outboxOutcome(outcome);
    return <ToneChip label={label.label} tone={label.tone} sandbox />;
};

const Destinations = ({ destinations }: { destinations: OutboxDestination[] }) => (
    <Stack spacing={0.5}>
        {destinations.map((d) => (
            <Stack key={d.entry_id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={mono}>{d.destination}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {outboxOutcome(d.outcome).label}{d.reason ? `: ${OUTBOX_REASONS[d.reason] || d.reason}` : ''}
                </Typography>
            </Stack>
        ))}
    </Stack>
);

const SendDetail = ({ sendId }: { sendId: number }) => {
    const api = useDeveloperApi();
    const [send, setSend] = React.useState<OutboxSendDetail | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const load = React.useCallback(() => {
        setError(null);
        api.outboxSend(sendId).then(setSend).catch(setError);
    }, [api, sendId]);
    React.useEffect(() => {
        setSend(null);
        load();
    }, [load]);
    if (error) {
        return <ErrorAlert error={error} onRetry={load} />;
    }
    if (!send) {
        return <Loading />;
    }
    return (
        <Box data-testid="outbox-detail">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Field label="Outcome"><OutcomeChip outcome={send.outcome} /></Field>
                <Field label="Created"><Timestamp value={send.created_ts} /></Field>
                <Field label="Channel">{OUTBOX_CHANNELS[send.channel] || send.channel}</Field>
                <Field label="Kind">{SEND_KINDS[send.send_kind] || send.send_kind}</Field>
                <Field label="Notification">{send.notification ? `${send.notification.name || '—'} (#${send.notification.id})` : '—'}</Field>
                <Field label="Event">{send.event?.code || '—'}</Field>
                <Field label="Booking id">{send.booking_id ?? '—'}</Field>
                <Field label="Request">
                    {send.request_id
                        ? <RouterLink to={`${DEVELOPER_PATHS.requests}/${send.request_id}`}><span style={mono}>{send.request_id}</span></RouterLink>
                        : '—'}
                </Field>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
                Destinations{send.destinations_unmasked ? '' : ' (masked)'}
            </Typography>
            <Destinations destinations={send.destinations} />
            {send.content.subject && (
                <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Subject</Typography>
                    <Typography variant="body2">{send.content.subject}</Typography>
                </>
            )}
            {send.content.html ? (
                <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Message</Typography>
                    <SandboxedHtmlPreview html={send.content.html} title="Rendered message" style={{ width: '100%', height: 420, border: '1px solid #e0e0e0', borderRadius: 4 }} />
                </>
            ) : null}
            {send.content.text && (
                <>
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>{send.content.html ? 'Text part' : 'Message'}</Typography>
                    <CodeBlock text={send.content.text} testId="outbox-text" />
                </>
            )}
        </Box>
    );
};

/** The sandbox's outbox (SBX D5, D9): every send, rendered, with what happened to each destination.
 *  Captured sends are expected -- shown neutral, never as errors. */
const OutboxPage = () => {
    const api = useDeveloperApi();
    const history = useHistory();
    const { sendId } = useParams<{ sendId?: string }>();
    const [outcome, setOutcome] = React.useState('');
    const [channel, setChannel] = React.useState('');
    const [bookingId, setBookingId] = React.useState('');
    const [destination, setDestination] = React.useState('');
    const [applied, setApplied] = React.useState({ booking_id: '', destination: '' });
    const bookingInvalid = !!bookingId.trim() && !/^\d+$/.test(bookingId.trim());

    const list = useCursorList<OutboxSend>(api.outbox, { outcome, channel, ...applied });

    const apply = (ev: React.FormEvent) => {
        ev.preventDefault();
        if (!bookingInvalid) {
            setApplied({ booking_id: bookingId.trim(), destination: destination.trim() });
        }
    };

    return (
        <DeveloperShell title="Outbox">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                What the sandbox sent or kept. Messages to anyone not on your allowlist are captured here instead of being delivered; kept 14 days.
            </Typography>
            <Box component="form" onSubmit={apply} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, my: 1.5 }}>
                <TextField select size="small" label="Outcome" value={outcome} onChange={(ev) => setOutcome(ev.target.value)} sx={{ minWidth: 200 }}>
                    <MenuItem value="">Any</MenuItem>
                    {Object.entries(OUTBOX_OUTCOMES).map(([value, { label }]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Channel" value={channel} onChange={(ev) => setChannel(ev.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="">Any</MenuItem>
                    {Object.entries(OUTBOX_CHANNELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField
                    size="small"
                    label="Booking id"
                    value={bookingId}
                    onChange={(ev) => setBookingId(ev.target.value)}
                    error={bookingInvalid}
                    helperText={bookingInvalid ? 'A number' : undefined}
                    sx={{ width: 140 }}
                />
                <TextField size="small" label="Exact destination" placeholder="e-mail, +E.164 phone or URL" value={destination} onChange={(ev) => setDestination(ev.target.value)} sx={{ minWidth: 260 }} />
                <Button type="submit" variant="outlined" disabled={bookingInvalid}>Search</Button>
            </Box>
            {list.error ? <ErrorAlert error={list.error} onRetry={list.reload} /> : null}
            {list.loading && list.items.length === 0 ? <Loading /> : (
                <TableScroll>
                    <Table size="small" data-testid="outbox">
                        <TableHead>
                            <TableRow>
                                <TableCell>Created</TableCell>
                                <TableCell>Outcome</TableCell>
                                <TableCell>Channel</TableCell>
                                <TableCell>To</TableCell>
                                <TableCell>Subject</TableCell>
                                <TableCell>Notification</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.items.map((row) => (
                                <TableRow
                                    key={row.id}
                                    hover
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => history.push(`${DEVELOPER_PATHS.outbox}/${row.id}`)}
                                    data-testid={`outbox-row-${row.id}`}
                                >
                                    <TableCell><Timestamp value={row.created_ts} /></TableCell>
                                    <TableCell><OutcomeChip outcome={row.outcome} /></TableCell>
                                    <TableCell>{OUTBOX_CHANNELS[row.channel] || row.channel}</TableCell>
                                    <TableCell sx={{ maxWidth: 280 }}><Destinations destinations={row.destinations} /></TableCell>
                                    <TableCell sx={{ maxWidth: 280 }}>{row.subject || '—'}</TableCell>
                                    <TableCell>{row.notification?.name || row.event?.code || '—'}</TableCell>
                                </TableRow>
                            ))}
                            {!list.loading && list.items.length === 0 && !list.error && (
                                <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">Nothing sent yet.</Typography></TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableScroll>
            )}
            <LoadMore hasMore={list.hasMore} loading={list.loading} onClick={list.loadMore} />
            <DetailDrawer open={!!sendId} onClose={() => history.push(DEVELOPER_PATHS.outbox)} title={<Typography variant="h6">Send #{sendId}</Typography>}>
                {sendId && /^\d+$/.test(sendId) ? <SendDetail sendId={Number(sendId)} /> : null}
            </DetailDrawer>
        </DeveloperShell>
    );
};

export default OutboxPage;
